/**
 * PublicTaskRow
 *
 * Rich, read-only task row for the public artifact list. Mirrors the
 * authenticated `TaskCard` (status dot + title + priority badge + assignee +
 * due date) but is fully prop-driven — no store, no navigation hooks — so it
 * works on the unauthenticated share/embed pages.
 *
 * @module components/share/public/PublicArtifactList/parts/PublicTaskRow
 */
import { memo, type CSSProperties } from 'react';
import { IconCalendar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common/StatusBadge';
import { UserAvatar } from '@/components/user/UserAvatar';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { ICON_SIZE_TAB } from '@/constants/ui';
import { STATUS_COLORS, TaskStatus } from '@/types';
import type { PublicTaskListItem } from '@/types/share';

import { PublicArtifactRowCard } from './PublicArtifactRowCard';
import styles from './PublicArtifactRows.module.css';

interface PublicTaskRowProps {
  task: PublicTaskListItem;
  onClick: () => void;
}

export const PublicTaskRow = memo(function PublicTaskRow({
  task,
  onClick,
}: PublicTaskRowProps) {
  const { t } = useTranslation();
  const { formatDateCompact, isOverdue } = useLocalizedDate();

  const dotStyle = {
    '--dot-color': STATUS_COLORS[task.status as TaskStatus],
  } as CSSProperties;

  const showOverdue =
    task.dueDate !== null &&
    isOverdue(task.dueDate) &&
    task.status !== TaskStatus.Completed;

  return (
    <PublicArtifactRowCard
      ariaLabel={`${t('tasks.openDetails')}: ${task.title}`}
      onClick={onClick}
    >
      <div className={styles.titleRow}>
        <span
          className={styles.statusDot}
          style={dotStyle}
          aria-hidden="true"
        />
        <span className={styles.title}>{task.title}</span>
        <div className={styles.badgeGroup}>
          <StatusBadge type="priority" value={task.priority} size="xs" />
        </div>
      </div>

      <div className={styles.metaRow}>
        {task.assigneeName !== null && (
          <div className={styles.metaGroup}>
            <UserAvatar displayName={task.assigneeName} size="xs" />
            <span className={styles.metaText}>{task.assigneeName}</span>
          </div>
        )}

        {task.dueDate !== null && (
          <div className={styles.metaGroup}>
            <IconCalendar size={ICON_SIZE_TAB} className={styles.metaIcon} />
            <span
              className={showOverdue ? styles.overdueText : styles.metaText}
            >
              {formatDateCompact(task.dueDate)}
            </span>
          </div>
        )}
      </div>
    </PublicArtifactRowCard>
  );
});
